<template>
    <div class="mb-5 pb-5">
        <div class="mt-5 flex flex-col md:flex-row justify-between items-start">
            <h1 class="text-3xl text-center md:text-left text-theme-base">
                Diseño de
                <strong class="text-theme-primary">tu catálogo</strong>
            </h1>
        </div>

        <section class="mt-8">
            <h2
                class="text-lg font-semibold text-theme-primary border-b border-theme-border pb-2"
            >
                Colores de tu catálogo
            </h2>
            <p class="mt-3 text-sm text-theme-muted">
                Elige una paleta lista para usar o ajusta cada color a tu gusto.
                Se aplican a todo tu catálogo.
            </p>

            <brand-color-picker
                v-model="form.stackColors"
                :business-name="tenantName"
                class="mt-4"
                default-mode="custom"
                more-palettes="modal"
            />
        </section>

        <section v-if="canChooseProductCard" class="mt-10">
            <h2
                class="text-lg font-semibold text-theme-primary border-b border-theme-border pb-2"
            >
                Tarjeta de producto
            </h2>
            <p class="mt-3 text-sm text-theme-muted">
                Elige cómo se ve cada producto en tu catálogo. Puedes cambiarla
                cuando quieras.
            </p>

            <product-item-design-selector
                :selected-id="form.productItemDesign"
                class="mt-4"
                @select="(option) => (form.productItemDesign = option.value)"
            />
        </section>

        <section v-if="showWatermark" class="mt-10">
            <h2
                class="text-lg font-semibold text-theme-primary border-b border-theme-border pb-2"
            >
                Marca de agua
            </h2>
            <p class="mt-3 text-sm text-theme-muted">
                Oculta el aviso “Crea tu catálogo gratis” que aparece al final
                de tu catálogo.
            </p>

            <div v-if="canUpdateWatermark" class="mt-4 flex items-center gap-3">
                <custom-switch v-model="form.catalogHideWatermark" success />
                <span class="text-sm text-theme-base">
                    {{
                        form.catalogHideWatermark
                            ? "Marca de agua oculta"
                            : "Marca de agua visible"
                    }}
                </span>
            </div>
            <div v-else class="mt-4 flex items-center gap-3 text-sm">
                <span class="text-theme-muted">🔒 No incluida en tu plan.</span>
                <button
                    type="button"
                    class="text-theme-primary underline"
                    @click="openWatermarkUpgrade"
                >
                    Ver planes
                </button>
            </div>
        </section>

        <div class="flex justify-center mt-10">
            <app-button
                :loading="isLoading"
                color="success"
                size="large"
                @click="updateSettings"
            >
                Actualizar
            </app-button>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import { displayModeShowsProducts } from "@/common/fields/display-modes";
import { canShowUpgrade } from "@/common/fields/plan-features";

import { DEFAULT_PRODUCT_CAROUSEL_VARIANT } from "@/components/catalog/product-sliders";
import BrandColorPicker from "@/components/editor/BrandColorPicker.vue";
import ProductItemDesignSelector from "@/components/editor/ProductItemDesignSelector.vue";
import CustomSwitch from "@/components/utils/CustomSwitch.vue";

const PRODUCT_CARD_PERMISSIONS = [
    "feature:ActivateProducts",
    "feature:ActivateDisplayModeCatalog",
    "feature:ActivateDisplayModeMix"
];

export default {
    name: "CustomizationDesignPage",
    components: { BrandColorPicker, CustomSwitch, ProductItemDesignSelector },
    data: () => ({
        isLoading: false,
        displayMode: null,
        form: {
            stackColors: {
                primary: "#284D6F",
                emphasis: "#FD951E",
                background: "#FFFFFF",
                text: "#000000"
            },
            productItemDesign: DEFAULT_PRODUCT_CAROUSEL_VARIANT,
            catalogHideWatermark: false
        }
    }),
    computed: {
        ...mapState("auth", ["tenantName", "permissions"]),
        ...mapGetters("control", ["backgroundColor"]),
        canUpdateWatermark() {
            return this.$ability.hasPermission(
                "tenant:UpdateWatermarkSettings"
            );
        },
        showWatermark() {
            return (
                this.canUpdateWatermark ||
                canShowUpgrade(this.permissions, "catalogHideWatermark")
            );
        },
        // La tarjeta de producto solo aplica en vistas que muestran productos.
        canChooseProductCard() {
            return (
                displayModeShowsProducts(this.displayMode) &&
                PRODUCT_CARD_PERMISSIONS.some((permission) =>
                    this.$ability.hasPermission(permission)
                )
            );
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("page:GetCatalog", "/app"))) return;
        await this.loadSettings();
    },
    methods: {
        ...mapMutations("control", ["SET_TENANT_STACK_COLORS"]),
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        openWatermarkUpgrade() {
            this.openPlanUpgrade({
                kind: "feature",
                feature: "catalogHideWatermark"
            });
        },
        ...mapActions("tenants", [
            "getCatalogPreview",
            "updateCatalogDesign",
            "getWatermarkSettings",
            "updateWatermarkSettings"
        ]),
        async loadSettings() {
            this.$loading.show();
            try {
                const [data, watermark] = await Promise.all([
                    this.getCatalogPreview(),
                    this.canUpdateWatermark &&
                    this.$ability.hasPermission("tenant:GetWatermarkSettings")
                        ? this.getWatermarkSettings()
                        : null
                ]);
                if (watermark) {
                    this.form.catalogHideWatermark =
                        watermark.catalogHideWatermark;
                }
                this.displayMode = data.displayMode;
                this.SET_TENANT_STACK_COLORS(data.stackColors);
                if (data.stackColors) {
                    this.form.stackColors = {
                        ...this.form.stackColors,
                        ...data.stackColors
                    };
                }
                this.form.productItemDesign =
                    data.productItemDesign ?? DEFAULT_PRODUCT_CAROUSEL_VARIANT;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateSettings() {
            this.isLoading = true;
            try {
                const payload = {
                    stackColors: { ...this.form.stackColors },
                    productItemDesign: this.form.productItemDesign
                };
                await this.updateCatalogDesign(payload);
                this.SET_TENANT_STACK_COLORS(payload.stackColors);
                if (this.canUpdateWatermark) {
                    await this.updateWatermarkSettings({
                        catalogHideWatermark: this.form.catalogHideWatermark
                    });
                }
                this.isLoading = false;
                this.$sound.play("success");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    text: "El diseño de tu catálogo ha sido guardado.",
                    icon: "success"
                });
            } catch (error) {
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
